import { FC, useEffect, useState } from "react";
import {
  stylesHeader,
  stylesHeaderSticky,
  stylesHeaderWrapper,
  stylesText,
  popperPanelBg,
  popperTriggerHover
} from "./styles";
import { HeaderProps } from "./types";
import ArrowLeftIcon from "@/nav-localized/assets/header/arrow-left-s-line.svg?react";
import Nav from "./Nav";
import { useDispatch, useStoreState } from "./store/useStore";
import User from "./User";
import { useTranslations } from "use-intl";
import ApplicationPopper from "./ApplicationPopper";
import LanguageIcon from "@/nav-localized/assets/header/language.svg?react";
import MoonIcon from "@/nav-localized/assets/header/moon.svg?react";
import SunIcon from "@/nav-localized/assets/header/sun.svg?react";
import MenuIcon from "@/nav-localized/assets/header/menu.svg?react";
import ActivityCryptonaryIcon from "@/nav-localized/assets/header/Activity-Cryptonary.svg?react";

import { LanguageContent } from "./LanguageDialog";
import PopOver from "./PopOver";
import useDemoTradingListen from "./hooks/useDemoTradingListen";
import usePageScrolled from "./hooks/usePageScrolled";
import useResponsiveHandlers from "./hooks/useResponsiveHandlers";
import { dispatchChatbotParamsSync, updateGlobalTheme } from "../common/CustomEvent";
import Cookies from "js-cookie";
import useKolActivityCheck from "./hooks/useKolActivityCheck";
import Logo from "@/nav-localized/Logo";
import { stylesDemoTrading, stylesLiveTrading } from "@/nav-localized/style";
import clsx from "clsx";
import { FuturesSettingsAction } from "./FuturesActions";
import { SpotSettingsAction } from "./SpotActions";

const Header: FC<HeaderProps> = (props) => {
  const { customChildren, wrapperClass, customChangeLang } = props;

  const {
    theme,
    pageTheme,
    localeLink,
    isDemoTrading,
    showTheme,
    changeTheme,
    position = "sticky",
    isSticky,
    themeLock,
    hideAllMenus,
    baseUrl
  } = useStoreState();

  const [thmeMode, setThemeMode] = useState(pageTheme ?? theme);

  useResponsiveHandlers(position);

  const { toLiveTrading } = useDemoTradingListen();

  const { dispatch } = useDispatch();

  const t = useTranslations("header");

  const { hasCompetence } = useKolActivityCheck(baseUrl);

  /** themeLock (home hero): background follows scroll, not store `isSticky` alone. */
  const pageScrolled = usePageScrolled(position === "sticky" && Boolean(themeLock));
  const showStickyBackground = position === "sticky" && (themeLock ? pageScrolled : isSticky);

  const updateTheme = () => {
    if (showTheme && changeTheme) {
      changeTheme((theme) => {
        if (!themeLock) {
          dispatch({ type: "SET_THEME", payload: { theme: theme } });
        }
        updateGlobalTheme(theme);
        setThemeMode(theme);
        dispatchChatbotParamsSync({ theme });
      });
    }
  };

  useEffect(() => {
    setThemeMode(pageTheme ?? theme);
  }, [pageTheme, theme]);

  useEffect(() => {
    const isSwitching = Cookies.get("isSwitching");
    if (isSwitching) {
      setTimeout(() => {
        Cookies.remove("isSwitching");
      }, 5000);
    }
  }, []);

  return (
    <div
      data-header-wrapper
      className={`${stylesHeaderWrapper()} overflow-visible ${
        showStickyBackground
          ? stylesHeaderSticky({ mode: theme })
          : position === "sticky"
            ? "bg-transparent"
            : stylesHeader({ mode: theme })
      } ${position === "sticky" ? "sticky" : "relative"} ${wrapperClass}`}
    >
      {!customChildren ? (
        <>
          <div className="flex items-center min-w-0" data-header-left>
            <a href={`${localeLink}/`} data-header-logo>
              <Logo theme={theme} />
            </a>
            {isDemoTrading && (
              <div className={stylesDemoTrading({ mode: theme })}>{t("demoTrading")}</div>
            )}
            {!hideAllMenus && <Nav />}
            {hasCompetence && (
              <div className="min-[860px]:block cursor-pointer select-none max-[860px]:hidden">
                <ActivityCryptonaryIcon
                  className="w-[120px] h-[42px] ml-[18px]"
                  onClick={() => {
                    window.location.href = `/activity${localeLink}/competition/d-cryptonary2`;
                  }}
                />
              </div>
            )}
          </div>
          <div className={`flex items-center justify-end h-full`} data-header-right>
            {isDemoTrading && (
              <div className={stylesLiveTrading({ mode: theme })} onClick={toLiveTrading}>
                <ArrowLeftIcon className="rtl-mirror w-[16px] h-[16px]" />
                <span className="ms-[8px]">{t("liveTrading")}</span>
              </div>
            )}
            <User />
            <div className="flex items-center max-[860px]:hidden">
              <ApplicationPopper />
              <PopOver
                trigger="group-hover"
                anchor="top-right"
                containerClassName={clsx(
                  "h-[56px] flex items-center",
                  stylesText({ mode: theme }),
                  popperTriggerHover({ mode: theme })
                )}
                content={
                  <div className={clsx("py-3 px-3 w-[320px]", popperPanelBg({ mode: theme }))}>
                    <LanguageContent customChangeLang={customChangeLang} />
                  </div>
                }
              >
                <LanguageIcon className={clsx("ml-[24px] w-[20px] h-[20px] cursor-pointer")} />
              </PopOver>
              <FuturesSettingsAction />
              <SpotSettingsAction />
              {showTheme && (
                <div
                  className={clsx(
                    "ml-[24px] h-[56px] flex items-center cursor-pointer",
                    stylesText({ mode: theme }),
                    popperTriggerHover({ mode: theme })
                  )}
                  onClick={updateTheme}
                >
                  {thmeMode === "light" ? (
                    <MoonIcon className={clsx(`w-[20px] h-[20px]`)} />
                  ) : (
                    <SunIcon className={clsx(`w-[20px] h-[20px]`)} />
                  )}
                </div>
              )}
            </div>
            <div
              className="ml-[24px] min-[860px]:hidden"
              onClick={() => dispatch({ type: "SET_DRAWEROPEN", payload: true })}
            >
              <MenuIcon
                className={`w-[24px] h-[24px] cursor-pointer ${stylesText({
                  mode: theme
                })}`}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          {customChildren}
          <PopOver
            trigger="group-hover"
            anchor="top-right"
            containerClassName={clsx(
              "h-[56px] flex items-center ml-[24px]",
              stylesText({ mode: theme }),
              popperTriggerHover({ mode: theme })
            )}
            content={
              <div className={clsx("p-[24px] w-[268px]", popperPanelBg({ mode: theme }))}>
                <LanguageContent customChangeLang={customChangeLang} />
              </div>
            }
          >
            <LanguageIcon className={`w-[20px] h-[20px] ml-[24px] cursor-pointer`} />
          </PopOver>
        </>
      )}
    </div>
  );
};

export default Header;
